import { supabase } from "@/lib/api"

/**
 * Signed URL cache entry
 */
interface CacheEntry {
  signedUrl: string
  expiresAt: number // Timestamp when the URL expires
}

/**
 * In-memory cache for signed URLs
 * Key format: `bucket:filePath`
 */
const urlCache = new Map<string, CacheEntry>()

/**
 * Generate cache key
 */
function getCacheKey(bucketName: string, filePath: string): string {
  return `${bucketName}:${filePath}`
}

/**
 * Check if a cache entry is still valid
 * We consider it valid if it has at least 5 minutes remaining before expiry
 */
function isValidEntry(entry: CacheEntry): boolean {
  const now = Date.now()
  const bufferMs = 5 * 60 * 1000 // 5 minutes buffer
  return entry.expiresAt > now + bufferMs
}

/**
 * Get a signed URL with caching
 *
 * @param bucketName - The Supabase storage bucket name
 * @param filePath - The file path within the bucket
 * @param expiresIn - URL expiration time in seconds (default: 3600 = 1 hour)
 * @returns The signed URL or null if error
 */
export async function getCachedSignedUrl(
  bucketName: string,
  filePath: string,
  expiresIn = 3600,
): Promise<string | null> {
  if (!filePath) {
    return null
  }

  // If it's already a full URL, use it directly (public bucket)
  if (filePath.startsWith("https://") || filePath.startsWith("http://")) {
    return filePath
  }

  // Check cache first
  const cacheKey = getCacheKey(bucketName, filePath)
  const cached = urlCache.get(cacheKey)

  if (cached && isValidEntry(cached)) {
    return cached.signedUrl
  }

  // Cache miss or expired - fetch new signed URL
  try {
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, expiresIn)

    if (error) {
      logError(`Error loading signed URL from ${bucketName}/${filePath}:`, error)
      // Remove invalid entry from cache
      urlCache.delete(cacheKey)
      return null
    }

    // Store in cache with expiration time
    const expiresAt = Date.now() + expiresIn * 1000
    urlCache.set(cacheKey, {
      signedUrl: data.signedUrl,
      expiresAt,
    })

    return data.signedUrl
  } catch (error) {
    logError(`Exception loading signed URL from ${bucketName}/${filePath}:`, error)
    return null
  }
}

/**
 * Load a profile picture URL from user_profiles bucket with caching
 */
export async function getCachedProfilePicture(picturePath: string | null): Promise<string | null> {
  if (!picturePath) return null
  return getCachedSignedUrl("user_profiles", picturePath)
}

/**
 * Load a cover image URL from user_profiles bucket with caching
 */
export async function getCachedCoverImage(coverPath: string | null): Promise<string | null> {
  if (!coverPath) return null
  return getCachedSignedUrl("user_profiles", coverPath)
}

/**
 * Invalidate a specific cached URL
 */
export function invalidateSignedUrl(bucketName: string, filePath: string): void {
  const cacheKey = getCacheKey(bucketName, filePath)
  urlCache.delete(cacheKey)
}

/**
 * Clear all cached URLs
 */
export function clearSignedUrlCache(): void {
  urlCache.clear()
}

/**
 * Get cache statistics (for debugging)
 */
export function getCacheStats() {
  return {
    size: urlCache.size,
    entries: Array.from(urlCache.keys()),
  }
}
import { error as logError } from "@/utils/debug"
